import { faGripVertical } from "@fortawesome/duotone-light-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  Checkbox,
  FlexBox,
  getDayAbbreviations,
  getRepetitionArrayFromRrule,
  getReptitionIntervalFromRrule,
  JoonUIColor,
  QuestSeries,
  SPACING,
  TherapistIcon,
  Typography,
} from "@joonapp/web-shared"
import dayjs from "dayjs"
import { useState } from "react"
import { rrulestr } from "rrule"

import useAllQuestsMultiSelectStore from "./hooks/useAllQuestsMultiSelectStore"
import { useQuestDndSectionsStore } from "./hooks/useQuestDndSectionsStore"
import UserBadge from "../../../components/badges/userBadge/UserBadge"
import { DragHandle } from "../../../components/dnd/DragHandle"
import useQuestBoardStore from "../useQuestBoardStore"
import { useQuestGroupSeries } from "../useQuestGroups"

type Props = {
  questSeries: QuestSeries
}

const QuestDndCard = ({ questSeries }: Props) => {
  const { selectedUserId } = useQuestBoardStore()
  const { selectedQuestSeriesIds, toggleQuestSeriesId } =
    useAllQuestsMultiSelectStore()
  const { recurrence, reminder_time, occurrence_limit, start_date } =
    questSeries || {}
  const { byweekday } = rrulestr(recurrence).options
  const { questGroupSeries } = useQuestGroupSeries(questSeries)
  const { userHasMadeChanges } = useQuestDndSectionsStore()

  const [isHovered, setIsHovered] = useState(false)
  const daysString =
    byweekday?.length === 7
      ? "Daily"
      : getDayAbbreviations(getRepetitionArrayFromRrule(recurrence))
  const frequency = getReptitionIntervalFromRrule(recurrence)

  const recurrenceText =
    occurrence_limit && start_date
      ? dayjs(start_date).format("M/D/YY")
      : `${daysString} every ${frequency > 1 ? frequency : ""} week${frequency > 1 ? "s" : ""}`
  const timeText = reminder_time ? ` at ${reminder_time}` : ""

  return (
    <div
      className="flex relative flex-row justify-between items-center gap-2 w-full p-3 border border-gray-200 rounded-md  bg-white transition-colors duration-200 touch-pan-y"
      style={{
        boxShadow: "4px 6px 6px 0px rgba(0, 0, 0, 0.08)",
        background: isHovered
          ? JoonUIColor.background.xlightGray
          : JoonUIColor.background.primaryNeutral,
        paddingLeft: selectedQuestSeriesIds.length > 0 ? "12px" : "40px",
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {questSeries.is_active && selectedQuestSeriesIds.length === 0 && (
        <DragHandle
          style={{
            position: "absolute",
            left: "-8px",
            top: "50%",
            padding: SPACING.space6,
            paddingRight: SPACING.space10,
            transform: "translateY(-50%)",
            cursor: "grab",
            zIndex: 1000,
          }}
        >
          <FontAwesomeIcon
            icon={faGripVertical}
            fontSize={SPACING.space4}
            color={JoonUIColor.icon.neutral}
          />
        </DragHandle>
      )}
      <FlexBox
        direction="column"
        align="center"
        style={{ overflowX: "hidden", position: "relative" }}
      >
        {recurrence || reminder_time ? (
          <Typography variant="bodyXSmall" textAlign="left">
            {recurrenceText + timeText}
          </Typography>
        ) : null}
        <Typography
          variant="bodySmallBold"
          textAlign="left"
          style={{
            textOverflow: "ellipsis",
            width: "100%",
            overflow: "hidden",
            whiteSpace: "nowrap",
          }}
        >
          <QuestCardTitle questSeries={questSeries} />
        </Typography>
        {selectedUserId === null && (
          <FlexBox direction="row" gap={SPACING.space1}>
            {questGroupSeries.map((series) => (
              <UserBadge key={series.id} userId={series.user_id} />
            ))}
          </FlexBox>
        )}
      </FlexBox>
      {!userHasMadeChanges && (
        <Checkbox
          name={`quest-dnd-card-${questSeries.id}`}
          selected={selectedQuestSeriesIds.includes(questSeries.id)}
          onChange={() => toggleQuestSeriesId(questSeries.id)}
          hideBorder
        />
      )}
    </div>
  )
}

export default QuestDndCard

export const QuestCardTitle = ({
  questSeries,
}: {
  questSeries: QuestSeries
}) => {
  return (
    <FlexBox align="center" gap={SPACING.space1} wrap={false}>
      {questSeries.current_frequency_target && (
        <TherapistIcon size={16} color={JoonUIColor.background.accent} />
      )}
      <Typography
        variant="bodySmallBold"
        style={{
          width: "100%",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {questSeries.title}
      </Typography>
    </FlexBox>
  )
}
